import { Injectable } from '@angular/core';
import { Filter, CustomRule, DisplayOptions, AdvancedConditions } from '../../models/filter-models';
import { flattenCosmeticCustomRulesInOrder, flattenCustomRulesInOrder } from '../../common/custom-rules/custom-rule-groups-helper';
import { YesNoAny } from "../../models/enums";
import { DisplayType, EquipmentTierType } from "../../models/enums";
import { Comparator, Rarity } from "../../models/enums";
import { WeaponType, ArmourType, SocketedItemType, BaseTypeTier } from "../../models/enums";
import { Tier } from '../../models/item-data-models';
import { EquipmentType } from '../../models/equipment-type.enum';
import { RuleGeneratorService } from './rule-generator.service';
import { poe1, poe2 } from '../../../game';
import { ItemDataService } from '../item-data.service';
import { GenerationHelperService } from './generation-helper.service';

export const awakenedGems = ["Awakened Enlighten Support", "Awakened Empower Support", "Awakened Enhance Support"];
export const specialSupportGems = ["Enlighten Support", "Empower Support", "Enhance Support"]; // Vaal Breach

export const topTierExceptionalGems = [
  "Greater Multistrike Support",
  "Greater Spell Echo Support",
  "Cooldown Recovery Support",
  "Greater Fork Support",
  "Eclipse Support",
  "Greater Devour Support",
  "Greater Ancestral Call Support",
  "Invert the Rules Support",
  "Cast on Ward Break Support",
  "Companionship Support",
  "Frostmage Support",
  "Greater Spell Cascade Support",
  "Bloodsoaked Banner Support",
  "Greater Unleash Support",
  "Greater Chain Support",
  "Annihilation Support",
  "Congregation Support",
  "Pact of Beidat",
  "Pact of Ghorr",
  "Pact of K'Tash",
  "Pact of Lycia",
];

export const otherExceptionalGems: string[] = [
  "Vaal Temptation Support",
  "Unholy Trinity Support",
  "Eldritch Blasphemy Support",
  "Scornful Herald Support",
  "Cull the Weak Support",
  "Divine Sentinel Support",
  "Pacifism Support",
  "Communion Support",
  "Overloaded Intensity Support",
  "Transfusion Support",
  "Vaal Sacrifice Support",
  "Voidstorm Support",
  "Foulgrasp Support",
  "Hextoad Support",
  "Hiveborn Support",
  "Machinations Support",
  "Pyre Support",
  "Bonespire Support",
  "Fissure Support",
  "Hexpass Support",
  "Lethal Dose Support",
  "Invention Support",
  "Greater Kinetic Instability Support",
  "Gluttony Support",
  "Overheat Support",
  "Void Shockwave Support",
  "Crystalfall Support",
  "Coursing Current Support",
];

export interface LevelingGoldThreshold {
  maxAreaLevel?: number;
  hideBelow: number;
  highlightAbove: number;
}

export const levelingGoldThresholds: LevelingGoldThreshold[] = poe2 ? [
  { maxAreaLevel: 20, hideBelow: 0, highlightAbove: 75 },
  { maxAreaLevel: 40, hideBelow: 5, highlightAbove: 100 },
  { maxAreaLevel: 60, hideBelow: 15, highlightAbove: 250 },
  { maxAreaLevel: 70, hideBelow: 40, highlightAbove: 500 },
  { maxAreaLevel: undefined, hideBelow: 75, highlightAbove: 1000 },
] : [
  { maxAreaLevel: 20, hideBelow: 0, highlightAbove: 50 },
  { maxAreaLevel: 40, hideBelow: 5, highlightAbove: 100 },
  { maxAreaLevel: 60, hideBelow: 15, highlightAbove: 150 },
  { maxAreaLevel: 70, hideBelow: 50, highlightAbove: 200 },
  { maxAreaLevel: undefined, hideBelow: 75, highlightAbove: 300 },
];

export const levelingVerisiumThresholds: LevelingGoldThreshold[] = [
  { maxAreaLevel: 20, hideBelow: 0, highlightAbove: 10 },
  { maxAreaLevel: 40, hideBelow: 0, highlightAbove: 20 },
  { maxAreaLevel: 60, hideBelow: 0, highlightAbove: 40 },
  { maxAreaLevel: 70, hideBelow: 5, highlightAbove: 50 },
  { maxAreaLevel: undefined, hideBelow: 10, highlightAbove: 75 },
];

@Injectable({
  providedIn: 'root'
})
export class GeneralGeneratorService {
  constructor(private ruleGenerator: RuleGeneratorService, private itemDataService: ItemDataService, private helper: GenerationHelperService) { }

  public generateGoldRules(filter: Filter) {
    const results: string[] = [];

    const hideGoldEnabled = filter.hideGold && filter.hideGoldLowerThan && !filter.showLevelingGold;
    const largeGoldEnabled = filter.showLargeGoldStacks && filter.showLargeGoldStacksMinStackSize;
    const hugeGoldEnabled = filter.showHugeGoldStacks && filter.showHugeGoldStacksMinStackSize;
    const levelingGoldEnabled = filter.showLevelingGold;

    if (!hideGoldEnabled && !largeGoldEnabled && !hugeGoldEnabled && !levelingGoldEnabled) return '';

    if (levelingGoldEnabled) {
      for (let i = 0; i < levelingGoldThresholds.length; i++) {
        const threshold = levelingGoldThresholds[i];
        const maxAreaLevel = threshold.maxAreaLevel;

        const advancedConditions: AdvancedConditions = {
          ...(maxAreaLevel !== undefined && { maxAreaLevel }),
        };

        const largeGoldRule = new CustomRule({
          baseTypes: ['Gold'],
          advancedConditions: new AdvancedConditions({
            ...advancedConditions,
            minStackSize: threshold.highlightAbove,
          }),
        });

        if (poe2) {
          largeGoldRule.itemClass = 'Stackable Currency';
        }

        results.push(this.ruleGenerator.generateEquipmentRule(
          EquipmentType.LargeGoldStacks,
          largeGoldRule,
          filter,
          `Large Gold Stack${ maxAreaLevel !== undefined ? ' Area Level ≤ ' + maxAreaLevel : ''}`,
          undefined,
          'gold-leveling'
        ));

        const normalMinStack = Math.max(1, threshold.hideBelow);
        const normalGoldRule = new CustomRule({
          baseTypes: ['Gold'],
          advancedConditions: new AdvancedConditions({
            ...advancedConditions,
            minStackSize: normalMinStack,
          }),
        });

        if (poe2) {
          normalGoldRule.itemClass = 'Stackable Currency';
        }

        results.push(this.ruleGenerator.generateEquipmentRule(
          EquipmentType.NormalGoldStacks,
          normalGoldRule,
          filter,
          `Normal Gold Stack${ maxAreaLevel !== undefined ? ' Area Level ≤ ' + maxAreaLevel : ''}`,
          undefined,
          'gold-leveling'
        ));

        if (threshold.hideBelow > 0) {
          const hideSmallRule = new CustomRule({
            baseTypes: ['Gold'],
            displayOptions: { hidden: true },
            advancedConditions: new AdvancedConditions(advancedConditions),
          });

          if (poe2) {
            hideSmallRule.itemClass = 'Stackable Currency';
          }

          hideSmallRule.displayOptions!.displayTypeTierHighlightType = EquipmentTierType.Gold;

          results.push(this.ruleGenerator.generateRule(
            hideSmallRule,
            filter,
            'gold-leveling',
            `Small Gold Stack${ maxAreaLevel !== undefined ? ' Area Level ≤ ' + maxAreaLevel : ''}`,
          ));
        }
      }

      return results.join('\n\n');
    }

    // Regular rules: always apply large/huge stack settings

    if (hugeGoldEnabled) {
      const hugeGoldStacksRule = new CustomRule({
        baseTypes: ['Gold'],
        advancedConditions: new AdvancedConditions({ minStackSize: filter.showHugeGoldStacksMinStackSize }),
      });

      if (poe2) {
        hugeGoldStacksRule.itemClass = 'Stackable Currency';
      }

      results.push(this.ruleGenerator.generateEquipmentRule(EquipmentType.HugeGoldStacks, hugeGoldStacksRule, filter, 'Huge Gold Stacks'));
    }

    // Large gold stacks rule (independent)
    if (largeGoldEnabled) {
      const largeGoldStacksRule = new CustomRule({
        baseTypes: ['Gold'],
        advancedConditions: new AdvancedConditions({ minStackSize: filter.showLargeGoldStacksMinStackSize }),
      });

      if (poe2) {
        largeGoldStacksRule.itemClass = 'Stackable Currency';
      }

      results.push(this.ruleGenerator.generateEquipmentRule(EquipmentType.LargeGoldStacks, largeGoldStacksRule, filter, 'Large Gold Stacks'));
    }

    if (hideGoldEnabled) {
      const hideSmallStacksRule = new CustomRule({
        baseTypes: ['Gold'],
        displayOptions: { hidden: true },
      });

      const normalGoldStacksRule = new CustomRule({
        baseTypes: ['Gold'],
        advancedConditions: new AdvancedConditions({ minStackSize: filter.hideGoldLowerThan }),
      });

      if (poe2) {
        hideSmallStacksRule.itemClass = 'Stackable Currency';
        normalGoldStacksRule.itemClass = 'Stackable Currency';
      }

      hideSmallStacksRule.displayOptions.displayTypeTierHighlightType = EquipmentTierType.Gold;

      results.push(this.ruleGenerator.generateEquipmentRule(EquipmentType.NormalGoldStacks, normalGoldStacksRule, filter, 'Normal Gold Stacks'));

      results.push(this.ruleGenerator.generateRule(hideSmallStacksRule, filter, 'gold-hide-small', 'Hide Small Gold Stacks', { customLine: `
  StackSize <= ${filter.hideGoldLowerThan}` }));
    }

    return results.join('\n\n');
  }

  public generateVerisiumRules(filter: Filter) {
    if (!poe2) return '';

    const results: string[] = [];

    const hideVerisiumEnabled = filter.hideVerisium && filter.hideVerisiumLowerThan && !filter.showLevelingVerisium;
    const largeVerisiumEnabled = filter.showLargeVerisiumStacks && filter.showLargeVerisiumStacksMinStackSize;
    const hugeVerisiumEnabled = filter.showHugeVerisiumStacks && filter.showHugeVerisiumStacksMinStackSize;
    const levelingVerisiumEnabled = filter.showLevelingVerisium;

    if (!hideVerisiumEnabled && !largeVerisiumEnabled && !hugeVerisiumEnabled && !levelingVerisiumEnabled) return '';

    if (levelingVerisiumEnabled) {
      for (let i = 0; i < levelingVerisiumThresholds.length; i++) {
        const threshold = levelingVerisiumThresholds[i];
        const maxAreaLevel = threshold.maxAreaLevel;

        const advancedConditions: AdvancedConditions = {
          ...(maxAreaLevel !== undefined && { maxAreaLevel }),
        };

        const largeVerisiumRule = new CustomRule({
          baseTypes: ['Verisium'],
          itemClass: 'Stackable Currency',
          advancedConditions: new AdvancedConditions({
            ...advancedConditions,
            minStackSize: threshold.highlightAbove,
          }),
        });

        results.push(this.ruleGenerator.generateEquipmentRule(
          EquipmentType.LargeVerisiumStacks,
          largeVerisiumRule,
          filter,
          `Large Verisium Stack${ maxAreaLevel !== undefined ? ' Area Level ≤ ' + maxAreaLevel : ''}`,
          undefined,
          'verisium-leveling'
        ));

        const normalMinStack = Math.max(1, threshold.hideBelow);
        const normalVerisiumRule = new CustomRule({
          baseTypes: ['Verisium'],
          itemClass: 'Stackable Currency',
          advancedConditions: new AdvancedConditions({
            ...advancedConditions,
            minStackSize: normalMinStack,
          }),
        });

        results.push(this.ruleGenerator.generateEquipmentRule(
          EquipmentType.NormalVerisiumStacks,
          normalVerisiumRule,
          filter,
          `Normal Verisium Stack${ maxAreaLevel !== undefined ? ' Area Level ≤ ' + maxAreaLevel : ''}`,
          undefined,
          'verisium-leveling'
        ));

        if (threshold.hideBelow > 0) {
          const hideSmallRule = new CustomRule({
            baseTypes: ['Verisium'],
            itemClass: 'Stackable Currency',
            displayOptions: { hidden: true },
            advancedConditions: new AdvancedConditions(advancedConditions),
          });

          hideSmallRule.displayOptions!.displayTypeTierHighlightType = EquipmentTierType.Verisium;

          results.push(this.ruleGenerator.generateRule(
            hideSmallRule,
            filter,
            'verisium-leveling',
            `Small Verisium Stack${ maxAreaLevel !== undefined ? ' Area Level ≤ ' + maxAreaLevel : ''}`,
          ));
        }
      }

      return results.join('\n\n');
    }

    if (hugeVerisiumEnabled) {
      const hugeVerisiumStacksRule = new CustomRule({
        baseTypes: ['Verisium'],
        itemClass: 'Stackable Currency',
        advancedConditions: new AdvancedConditions({ minStackSize: filter.showHugeVerisiumStacksMinStackSize }),
      });

      results.push(this.ruleGenerator.generateEquipmentRule(EquipmentType.HugeVerisiumStacks, hugeVerisiumStacksRule, filter, 'Huge Verisium Stacks'));
    }

    if (largeVerisiumEnabled) {
      const largeVerisiumStacksRule = new CustomRule({
        baseTypes: ['Verisium'],
        itemClass: 'Stackable Currency',
        advancedConditions: new AdvancedConditions({ minStackSize: filter.showLargeVerisiumStacksMinStackSize }),
      });

      results.push(this.ruleGenerator.generateEquipmentRule(EquipmentType.LargeVerisiumStacks, largeVerisiumStacksRule, filter, 'Large Verisium Stacks'));
    }

    if (hideVerisiumEnabled) {
      const hideSmallStacksRule = new CustomRule({
        baseTypes: ['Verisium'],
        itemClass: 'Stackable Currency',
        displayOptions: { hidden: true },
      });

      const normalVerisiumStacksRule = new CustomRule({
        baseTypes: ['Verisium'],
        itemClass: 'Stackable Currency',
        advancedConditions: new AdvancedConditions({ minStackSize: filter.hideVerisiumLowerThan }),
      });

      hideSmallStacksRule.displayOptions.displayTypeTierHighlightType = EquipmentTierType.Verisium;

      results.push(this.ruleGenerator.generateEquipmentRule(EquipmentType.NormalVerisiumStacks, normalVerisiumStacksRule, filter, 'Normal Verisium Stacks'));

      results.push(this.ruleGenerator.generateRule(hideSmallStacksRule, filter, 'verisium-hide-small', 'Hide Small Verisium Stacks', { customLine: `
  StackSize <= ${filter.hideVerisiumLowerThan}` }));
    }

    return results.join('\n\n');
  }

  public generateQuestItemHighlightRules(filter: Filter) {
    const questItemsRule = !filter.showQuestItems ? '' : this.ruleGenerator.generateRule(new CustomRule({
      itemClass: poe1 ? 'Quest Items' : undefined,
      filterItemClass: poe2 ? '"Quest Items" "Instance Local Items"' : undefined,
    }), filter, 'quest-items', 'Quest Items');

    const questItemsRuleWatchstonesRule = poe2 ? '' : !filter.showQuestItems ? '' : this.ruleGenerator.generateRule(new CustomRule({
      itemClass: 'Atlas Upgrade Items',
    }), filter, 'quest-items', 'Watchstones');

    const questItemsRuleIncursionItemsRule = poe2 ? '' : !filter.showQuestItems ? '' : this.ruleGenerator.generateRule(new CustomRule({
      itemClass: 'Incursion Item',
    }), filter, 'quest-items', 'Incursion Items');

    const questItemsRuleLabyrinthKeysRule = poe2 ? '' : !filter.showQuestItems ? '' : this.ruleGenerator.generateRule(new CustomRule({
      itemClass: 'Labyrinth Items',
    }), filter, 'quest-items', 'Labyrinth Keys', {
      customBaseTypes: '"Golden Key" "Silver Key" "Treasure Key"'
    });

    return questItemsRule + '\n\n' + questItemsRuleWatchstonesRule + '\n\n' + questItemsRuleIncursionItemsRule + '\n\n' + questItemsRuleLabyrinthKeysRule;
  }

  public generateHeistHighlightRules(filter: Filter) {
    if (poe2) return '';

    const heistEnchantedBlueprintsRule = new CustomRule({
      itemClass: 'Blueprints',
      rarity: Rarity.Rare,
      rarityComparator: Comparator.LessThanOrEqual,
      advancedConditions: new AdvancedConditions({ anyEnchantment: true })
    });

    const heistEnchantedContractsRule = new CustomRule({
      itemClass: 'Contracts',
      rarity: Rarity.Rare,
      rarityComparator: Comparator.LessThanOrEqual,
      advancedConditions: new AdvancedConditions({ anyEnchantment: true })
    });

    const heistBlueprintsRule = new CustomRule({
      itemClass: 'Blueprints',
      rarity: Rarity.Rare,
      rarityComparator: Comparator.LessThanOrEqual
    });

    const heistContractsRule = new CustomRule({
      itemClass: 'Contracts',
      rarity: Rarity.Rare,
      rarityComparator: Comparator.LessThanOrEqual
    });

    if (filter.showHeistLocations?.length > 0 && !filter.showHeistLocations.includes('All')) {
      heistBlueprintsRule.baseTypes = filter.showHeistLocations.map(l => `Blueprint: ${l}`);
      heistContractsRule.baseTypes = filter.showHeistLocations.map(l => `Contract: ${l}`);
    }

    const roguesMarkersRule = new CustomRule({
      itemClass: 'Stackable Currency',
      baseTypes: ['Rogue\'s Marker'],
    });

    const enchantedRules = (filter.showHeistEnchantedBlueprints ? this.ruleGenerator.generateEquipmentRule(EquipmentType.ItemsHeistEnchantedBlueprints, heistEnchantedBlueprintsRule, filter, 'Enchanted Heist Blueprints', undefined, undefined, filter.showHeistEnchantedBlueprints) : '')
      + '\n\n' + (filter.showHeistEnchantedContracts ? this.ruleGenerator.generateEquipmentRule(EquipmentType.ItemsHeistEnchantedContracts, heistEnchantedContractsRule, filter, 'Enchanted Heist Contracts', undefined, undefined, filter.showHeistEnchantedContracts) : '');

    const heistRules = this.ruleGenerator.generateEquipmentRule(EquipmentType.ItemsHeistBlueprints, heistBlueprintsRule, filter, 'Heist Blueprints', undefined, undefined, filter.showHeistBlueprints)
      + '\n\n' + this.ruleGenerator.generateEquipmentRule(EquipmentType.ItemsHeistContracts, heistContractsRule, filter, 'Heist Contracts', undefined, undefined, filter.showHeistContracts)
      + '\n\n' + this.ruleGenerator.generateEquipmentRule(EquipmentType.ItemsRoguesMarkers, roguesMarkersRule, filter, 'Rogue\'s Markers', undefined, undefined, filter.showRoguesMarkers);

    return enchantedRules + '\n\n' + heistRules;
  }

  public generateGemHighlightRules(filter: Filter) {
    if (poe2) return '';

    const highlightTopTierAwakenedGemsRule = filter.showTopTierAwakenedGems ? this.ruleGenerator.generateEquipmentRule(EquipmentType.GemsTopTierAwakened, new CustomRule({
      baseTypes: awakenedGems,
    }), filter, 'Awakened Gems', {
      customClass: "\"Support Gems\""
    }) : '';

    const highlightTopTierSpecialSupportGemsRule = filter.showTopTierSpecialSupportGems ? this.ruleGenerator.generateEquipmentRule(EquipmentType.GemsTopTierSpecialSupport, new CustomRule({
      baseTypes: specialSupportGems,
    }), filter, 'Special Support Gems', {
      customClass: "\"Skill Gems\" \"Support Gems\""
    }) : '';

    const highlightTopTierExceptionalSupportGemsRule = filter.showTopTierExceptionalSupportGems ? this.ruleGenerator.generateEquipmentRule(EquipmentType.GemsTopTierExceptionalSupport, new CustomRule({
      baseTypes: topTierExceptionalGems,
    }), filter, 'Top Tier Exceptional Gems', {
      customClass: "\"Skill Gems\" \"Support Gems\""
    }) : '';

    const highlightOtherExceptionalSupportGemsRule = filter.showOtherExceptionalSupportGems && otherExceptionalGems.length > 0 ? this.ruleGenerator.generateEquipmentRule(EquipmentType.GemsOtherExceptionalSupport, new CustomRule({
      baseTypes: otherExceptionalGems,
    }), filter, 'Other Exceptional Gems', {
      customClass: "\"Skill Gems\" \"Support Gems\""
    }) : '';

    const highlightImbuedGemsRule = filter.showImbuedGems ? this.ruleGenerator.generateEquipmentRule(EquipmentType.GemsImbued, new CustomRule({
      advancedConditions: new AdvancedConditions({ imbuedGem: true }),
    }), filter, 'Imbued Gems', {
      customClass: "\"Skill Gems\"",
    }) : '';

    const highlightLvl21Quality20GemsRule = filter.showLvl21Quality20Gems ? this.ruleGenerator.generateEquipmentRule(EquipmentType.GemsLvl21Quality20, new CustomRule({
      advancedConditions: new AdvancedConditions({ minGemLevel: 21, minQuality: 20 }),
    }), filter, 'Level 21 / 20Q Gems', {
      customClass: "\"Skill Gems\" \"Support Gems\"",
    }) : '';

    const highlightLvl20Quality20GemsRule = filter.showLvl20Quality20Gems ? this.ruleGenerator.generateEquipmentRule(EquipmentType.GemsLvl20Quality20, new CustomRule({
      advancedConditions: new AdvancedConditions({ minGemLevel: 20, minQuality: 20 }),
    }), filter, 'Level 20 / 20Q Gems', {
      customClass: "\"Skill Gems\" \"Support Gems\"",
    }) : '';

    const highlightLvl21GemsRule = filter.showLvl21Gems ? this.ruleGenerator.generateEquipmentRule(EquipmentType.GemsLvl21, new CustomRule({
    }), filter, 'Level 21 Gems', {
      customClass: "\"Skill Gems\" \"Support Gems\"",
      customLine: `
  GemLevel >= 21`,
    }) : '';

    const highlightLevelGemsRule = filter.showLevelGems ? this.ruleGenerator.generateEquipmentRule(EquipmentType.GemsLevel, new CustomRule({
    }), filter, 'Level ' + filter.showLevelGemsMinLevel + '+ Gems', {
      customClass: "\"Skill Gems\" \"Support Gems\"",
      customLine: `
  GemLevel >= ${filter.showLevelGemsMinLevel}`,
    }) : '';

    const highlightQuality23GemsRule = filter.showQuality23Gems ? this.ruleGenerator.generateEquipmentRule(EquipmentType.GemsQuality23, new CustomRule({
      advancedConditions: new AdvancedConditions({ minQuality: 23 }),
    }), filter, '23+ Quality Gems', {
      customClass: "\"Skill Gems\" \"Support Gems\""
    }) : '';

    const highlightQuality10PlusGemsRule = (filter.showQualityGems && filter.showQualityGemsMinQuality) ? this.ruleGenerator.generateEquipmentRule(EquipmentType.GemsQuality, new CustomRule({
      advancedConditions: new AdvancedConditions({ minQuality: filter.showQualityGemsMinQuality }),
    }), filter, filter.showQualityGemsMinQuality + '+ Quality Gems', {
      customClass: "\"Skill Gems\" \"Support Gems\""
     }) : '';

    const highlightOtherQualityGemsRule = filter.showOtherQualityGems ? this.ruleGenerator.generateEquipmentRule(EquipmentType.GemsOtherQuality, new CustomRule({
      advancedConditions: new AdvancedConditions({ minQuality: 1 }),
    }), filter, 'Other Quality Gems', {
      customClass: "\"Skill Gems\" \"Support Gems\""
    }) : '';

    const highlightSearchedGemsRule = filter.showSearchedGems && filter.showSearchedGemTypes.length > 0 && !filter.showSearchedGemTypes.includes('None') ? this.ruleGenerator.generateEquipmentRule(EquipmentType.GemsSearched, new CustomRule({
      baseTypes: filter.showSearchedGemTypes,
    }), filter, 'Favourite Gems', {
      customClass: "\"Skill Gems\" \"Support Gems\""
     }) : '';

    const hideOtherGems = filter.hideOtherGems ? `
Show
  TransfiguredGem True
  Class == "Skill Gems" "Support Gems"

${filter.hideOtherGemsExceptVaalGems ? `Show
  Class == "Skill Gems" "Support Gems"
  BaseType "Vaal "` : ''}

Hide
  Class == "Skill Gems" "Support Gems"
` : '';

    return highlightTopTierAwakenedGemsRule
    + '\n\n' + highlightTopTierSpecialSupportGemsRule
    + '\n\n' + highlightTopTierExceptionalSupportGemsRule + '\n\n' + highlightOtherExceptionalSupportGemsRule
    + '\n\n' + highlightImbuedGemsRule
    + '\n\n' + highlightLvl21Quality20GemsRule + '\n\n' + highlightLvl20Quality20GemsRule
    + '\n\n' + highlightLvl21GemsRule + '\n\n' + highlightLevelGemsRule + '\n\n'
    + '\n\n' + highlightQuality23GemsRule + '\n\n' + highlightQuality10PlusGemsRule + '\n\n' + highlightOtherQualityGemsRule
    + '\n\n' + highlightSearchedGemsRule
    + '\n\n' + hideOtherGems;
  }

  public generate6LinkedItemRule(filter: Filter) {
    if (poe2 || !filter.show6LinkedGear) return '';
    const { baseTypes, minItemLevel } = this.linkedGearConditions(filter);
    const sixLinkedItemRule = this.ruleGenerator.generateEquipmentRule(EquipmentType.OtherGear6LinkedItems, new CustomRule({
      advancedConditions: new AdvancedConditions({ minLinks: 6, corrupted: YesNoAny.No, minItemLevel }),
      rarity: Rarity.Rare,
      rarityComparator: Comparator.LessThanOrEqual,
      baseTypes,
    }), filter, 'Six-Linked Gear');
    return sixLinkedItemRule;
  }

  public generate5LinkedItemRule(filter: Filter) {
    if (poe2 || !filter.show5LinkedGear) return '';
    const { baseTypes, minItemLevel } = this.linkedGearConditions(filter);
    const fiveLinkedItemRule = this.ruleGenerator.generateEquipmentRule(EquipmentType.OtherGear5LinkedItems, new CustomRule({
      advancedConditions: new AdvancedConditions({ minLinks: 5, corrupted: YesNoAny.No, minItemLevel }),
      rarity: Rarity.Rare,
      rarityComparator: Comparator.LessThanOrEqual,
      baseTypes,
    }), filter, 'Five-Linked Gear');
    return fiveLinkedItemRule;
  }

  public generate6ColourItemRule(filter: Filter) {
    return this.generateColourSocketItemRules(filter, 6, EquipmentType.OtherGear6ColourItems, 'Six-Colour Gear', filter.show6ColourGear);
  }

  public generate5ColourItemRule(filter: Filter) {
    return this.generateColourSocketItemRules(filter, 5, EquipmentType.OtherGear5ColourItems, 'Five-Colour Gear', filter.show5ColourGear);
  }

  public generate4ColourItemRule(filter: Filter) {
    return this.generateColourSocketItemRules(filter, 4, EquipmentType.OtherGear4ColourItems, 'Four-Colour Gear', filter.show4ColourGear);
  }

  private linkedGearConditions(filter: Filter) {
    const baseTypes = filter.show6LinkedGearMinBaseTypeTier === BaseTypeTier.Basic ? [] :
      this.helper.buildBaseTypesForTier(filter.show6LinkedGearMinBaseTypeTier, [WeaponType.All], [ArmourType.All]);
    return { baseTypes, minItemLevel: filter.show6LinkedGearMinItemLevel || undefined };
  }

  private colourGearConditions(filter: Filter) {
    const baseTypes = filter.show6ColourGearMinBaseTypeTier === BaseTypeTier.Basic ? [] :
      this.helper.buildBaseTypesForTier(filter.show6ColourGearMinBaseTypeTier, [WeaponType.All], [ArmourType.All]);
    return { baseTypes, minItemLevel: filter.show6ColourGearMinItemLevel || undefined };
  }

  private generateColourSocketItemRules(
    filter: Filter,
    colouredCount: number,
    equipmentType: EquipmentType,
    name: string,
    enabled: boolean,
  ) {
    if (poe2 || !enabled) return '';

    const { baseTypes, minItemLevel } = this.colourGearConditions(filter);
    let result = '';
    for (let totalSockets = colouredCount; totalSockets <= 6; totalSockets++) {
      const maxWhites = totalSockets - colouredCount;
      const whiteCondition = maxWhites === 0
        ? 'Sockets < W'
        : `Sockets <= ${'W'.repeat(maxWhites)}`;
      result += this.ruleGenerator.generateEquipmentRule(equipmentType, new CustomRule({
        advancedConditions: new AdvancedConditions({ corrupted: YesNoAny.No, minItemLevel }),
        rarity: Rarity.Rare,
        rarityComparator: Comparator.LessThanOrEqual,
        baseTypes,
      }), filter, name, {
        customLine: `
  Sockets = ${totalSockets}
  ${whiteCondition}`
      }) + '\n\n';
    }
    return result.trimEnd();
  }

  public static formatSocketGroup(socketGroup: string) {
    if (!socketGroup) return '';
    if (socketGroup.includes('X')) {
      return socketGroup.length + socketGroup.replaceAll('X', '');
    }
    return socketGroup;
  }

  public generateQualityGearRules(filter: Filter) {
    let weaponsQuality = '';
    if (poe1) {
      if (filter.showQualityWeapons) {
        const sizes = this.helper.getSizesByGearSizeType(filter.showQualityWeaponsSize);
        const sizeDescription = this.helper.getSizeDescription(filter.showQualityWeaponsSize);
        sizes.forEach(size => {
          weaponsQuality += this.ruleGenerator.generateEquipmentRule(EquipmentType.OtherGearSalvagingRecipeQualityWeapons, new CustomRule({
            advancedConditions: new AdvancedConditions({ minQuality: filter.showQualityWeaponsMinQuality || 1 }),
          }), filter,  sizeDescription + ' Quality Weapons', {
            customClass: this.itemDataService.getAllWeaponTypes().map(g => `"${g}"`).join(' '),
            customLine: size
          }) + '\n\n';
        });
      }
    }

    let martialWeaponsQuality = '';
    if (poe2) {
      if (filter.showQualityMartialWeapons) {
        const sizes = this.helper.getSizesByGearSizeType(filter.showQualityMartialWeaponsSize);
        const sizeDescription = this.helper.getSizeDescription(filter.showQualityMartialWeaponsSize);
        sizes.forEach(size => {
          martialWeaponsQuality += this.ruleGenerator.generateEquipmentRule(EquipmentType.OtherGearSalvagingQualityMartialWeapons, new CustomRule({
            advancedConditions: new AdvancedConditions({ minQuality: filter.showQualityMartialWeaponsMinQuality || 1 }),
          }), filter,  sizeDescription + ' Quality Martial Weapons', {
            customClass: this.formatAllMartialWeapons().map(g => `"${g}"`).join(' '),
            customLine: size
          }) + '\n\n';
        });
      }
    }

    let casterWeaponsQuality = '';
    if (poe2) {
      if (filter.showQualityCasterWeapons) {
        const sizes = this.helper.getSizesByGearSizeType(filter.showQualityCasterWeaponsSize);
        const sizeDescription = this.helper.getSizeDescription(filter.showQualityCasterWeaponsSize);
        sizes.forEach(size => {
          casterWeaponsQuality += this.ruleGenerator.generateEquipmentRule(EquipmentType.OtherGearSalvagingQualityCasterWeapons, new CustomRule({
            advancedConditions: new AdvancedConditions({ minQuality: filter.showQualityCasterWeaponsMinQuality || 1 }),
          }), filter,  sizeDescription + ' Quality Caster Weapons', {
            customClass: this.formatAllCasterWeapons().map(g => `"${g}"`).join(' '),
            customLine: size
          }) + '\n\n';
        });
      }
    }

    let armourQuality = '';
    if (filter.showQualityArmour) {
      const sizes = this.helper.getSizesByGearSizeType(filter.showQualityArmourSize);
      const sizeDescription = this.helper.getSizeDescription(filter.showQualityArmourSize);
      const equipmentType = poe1 ? EquipmentType.OtherGearSalvagingRecipeQualityArmour : EquipmentType.OtherGearSalvagingQualityArmour;
      sizes.forEach(size => {
        armourQuality += this.ruleGenerator.generateEquipmentRule(equipmentType, new CustomRule({
          advancedConditions: new AdvancedConditions({ minQuality: filter.showQualityArmourMinQuality || 1 }),
        }), filter,  sizeDescription + ' Quality Armour', {
          customClass: this.itemDataService.getAllArmourTypes().map(g => `"${g}"`).join(' '),
          customLine: size
        }) + '\n\n';
      });
    }

    return weaponsQuality + '\n' + martialWeaponsQuality + '\n' + casterWeaponsQuality + '\n' + armourQuality;
  }

  public generateSocketedGearRules(filter: Filter) {
    if (poe1 || !filter.showSocketedGear) return '';

    let socketedGearRules = '';

    if (filter.showSocketedGear) {
      socketedGearRules = this.ruleGenerator.generateEquipmentRule(EquipmentType.OtherGearSalvagingSocketed, new CustomRule({
        advancedConditions: new AdvancedConditions({ minSockets: filter.showSocketedGearType === SocketedItemType.TwoOrMoreSockets ? 2 : 1 }),
      }), filter, 'Socketed Gear');
    }

    return socketedGearRules;
  }

  public generateTrialItemHighlightRules(filter: Filter) {
    const goldKeyOptions = filter.customRuleStyles.find(s => s.ruleId === 'trial-gold-key')!.options!;
    const silverKeyOptions = filter.customRuleStyles.find(s => s.ruleId === 'trial-silver-key')!.options!;
    const bronzeKeyOptions = filter.customRuleStyles.find(s => s.ruleId === 'trial-bronze-key')!.options!;

    const bronzeKeyRule = poe1 ? '' : !filter.showTrialKeys ? '' : this.ruleGenerator.generateRule(new CustomRule({
      baseTypes: ['Bronze Key'],
      displayOptions: new DisplayOptions({ displayType: DisplayType.Custom, cosmeticOptions: bronzeKeyOptions }),
    }), filter, 'trial-bronze-key', 'Bronze Key');

    const silverKeyRule = poe1 ? '' : !filter.showTrialKeys ? '' : this.ruleGenerator.generateRule(new CustomRule({
      baseTypes: ['Silver Key'],
      displayOptions: new DisplayOptions({ displayType: DisplayType.Custom, cosmeticOptions: silverKeyOptions }),
    }), filter, 'trial-silver-key', 'Silver Key');

    const goldKeyRule = poe1 ? '' : !filter.showTrialKeys ? '' : this.ruleGenerator.generateRule(new CustomRule({
      baseTypes: ['Gold Key'],
      displayOptions: new DisplayOptions({ displayType: DisplayType.Custom, cosmeticOptions: goldKeyOptions }),
    }), filter, 'trial-gold-key', 'Gold Key');

    const relicSelections = filter.showTrialItemTypes.filter(t => t !== 'All');
    const relicBaseTypes = relicSelections.length === 0 ? ['All'] : relicSelections;

    const relicRule = !filter.showTrialItems ? '' : this.ruleGenerator.generateEquipmentRule(EquipmentType.ItemsTrialItems, new CustomRule({
      itemClass: 'Relics',
      rarity: Rarity.Magic,
      rarityComparator: Comparator.LessThanOrEqual,
      baseTypes: relicBaseTypes,
    }), filter, 'Relics', undefined, undefined, filter.showTrialItems);

    const hideOtherTrialRelicsRule = filter.showTrialItems && filter.hideOtherTrialItems ? this.ruleGenerator.generateRule(new CustomRule({
      itemClass: 'Relics',
      rarity: Rarity.Magic,
      rarityComparator: Comparator.LessThanOrEqual,
      displayOptions: new DisplayOptions({ hidden: true }),
    }), filter, 'trial-hide-other-relics', 'Hide Other Trial Relics') : '';

    return relicRule + '\n\n' + hideOtherTrialRelicsRule + '\n\n' + bronzeKeyRule + '\n\n' + silverKeyRule + '\n\n' + goldKeyRule;
  }

  public generateCustomRules(filter: Filter) {
    return flattenCustomRulesInOrder(filter)
      .filter(r => r.displayOptions.active)
      .filter(r => !this.isEmptyRule(r))
      .map((r, index) => {
        const name = r.label?.trim() || this.getCustomRuleName(r, false, index);
        return this.ruleGenerator.generateRule(r, filter, 'custom-rule', name, undefined, undefined, 'custom', index);
      }).join('\n');
  }

  public getCustomRuleName(customRule: CustomRule, cosmetic: boolean, index: number) {
    const itemName = customRule.itemType === 'All' ? 'All' : customRule.baseTypes.length === 1 && customRule.baseTypes[0] !== 'All'
      ? customRule.baseTypes[0]
      : customRule.baseTypes.length === 1 && customRule.baseTypes[0] === 'All'
        ? 'All ' + customRule.itemType
        : customRule.baseTypes.length > 1 && !customRule.baseTypes.includes('All')
          ? customRule.baseTypes.join(', ')
          : customRule.itemType;

    return 'Custom ' + (cosmetic ? 'Cosmetic ' : '') + 'Rule ' + (index + 1) + ' for ' + itemName;
  }

  public generateCustomCosmeticRules(filter: Filter) {
    return flattenCosmeticCustomRulesInOrder(filter)
      .filter(r => r.displayOptions.active)
      .filter(r => !this.isEmptyRule(r))
      .map((r, index) => {
        const name = r.label?.trim() || this.getCustomRuleName(r, true, index);
        return this.ruleGenerator.generateRule(r, filter, 'custom-cosmetic-rule', name, undefined, undefined, 'cosmetic', index);
      }).join('\n');
  }

  private isEmptyRule(rule: CustomRule) {
    const hasAdvancedConditions = AdvancedConditions.hasChanges(rule.advancedConditions)
    const emptyType = rule.rarity == Rarity.Normal && rule.itemType === 'All' && rule.itemClass === 'All' && (rule.baseTypes.length === 0 || rule.baseTypes.includes('All') && !hasAdvancedConditions);
    const cosmetics = rule.displayOptions.cosmeticOptions;
    const noCosmetics = !cosmetics || (!cosmetics?.highlight && !cosmetics?.alertSound && !cosmetics?.disableDropSound &&
      !cosmetics?.customAlertSound && !cosmetics?.textColor && !cosmetics?.backgroundColor && !cosmetics?.borderColor && !cosmetics?.fontSize);
    return emptyType && noCosmetics;
  }

  private formatAllMartialWeapons() {
    return [WeaponType.Bows, WeaponType.Crossbows, WeaponType.Foci, WeaponType.OneHandMaces, WeaponType.Quarterstaves,
      WeaponType.Quivers, WeaponType.TwoHandMaces, WeaponType.Spears, WeaponType.Talismans].map(g => g.toString());
  }

  private formatAllCasterWeapons() {
    return [WeaponType.Sceptres, WeaponType.Staves, WeaponType.Wands].map(g => g.toString());
  }
}
